import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface ManualRoofMeasurementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  tradeId: string;
  orgId: string;
  address?: string;
  onSuccess: () => void;
}

export function ManualRoofMeasurementDialog({
  open,
  onOpenChange,
  jobId,
  tradeId,
  orgId,
  address,
  onSuccess,
}: ManualRoofMeasurementDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    total_squares: "",
    total_area: "",
    pitch: "",
    ridges: "",
    hips: "",
    valleys: "",
    eaves: "",
    rakes: "",
    waste_factor: "10",
    penetrations: "",
    stories: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data: userData } = await supabase.auth.getUser();
      
      const insertData: any = {
        job_id: jobId,
        job_trade_id: tradeId,
        org_id: orgId,
        address: address || null,
        created_by: userData.user?.id,
      };

      // Add numeric fields if provided
      if (formData.total_squares) insertData.total_squares = parseFloat(formData.total_squares);
      if (formData.total_area) insertData.total_area = parseFloat(formData.total_area);
      if (formData.pitch) insertData.pitch = formData.pitch;
      if (formData.ridges) insertData.ridges = parseFloat(formData.ridges);
      if (formData.hips) insertData.hips = parseFloat(formData.hips);
      if (formData.valleys) insertData.valleys = parseFloat(formData.valleys);
      if (formData.eaves) insertData.eaves = parseFloat(formData.eaves);
      if (formData.rakes) insertData.rakes = parseFloat(formData.rakes);
      if (formData.waste_factor) insertData.waste_factor = parseFloat(formData.waste_factor);
      if (formData.penetrations) insertData.penetrations = parseInt(formData.penetrations);
      if (formData.stories) insertData.stories = parseInt(formData.stories);

      // Calculate total_area from total_squares if not provided
      if (!insertData.total_area && insertData.total_squares) {
        insertData.total_area = insertData.total_squares * 100;
      }
      
      // Calculate total_squares from total_area if not provided
      if (!insertData.total_squares && insertData.total_area) {
        insertData.total_squares = insertData.total_area / 100;
      }

      const { error } = await supabase.from("measurements_roof").insert(insertData);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Roof measurements saved successfully",
      });

      setFormData({
        total_squares: "",
        total_area: "",
        pitch: "",
        ridges: "",
        hips: "",
        valleys: "",
        eaves: "",
        rakes: "",
        waste_factor: "10",
        penetrations: "",
        stories: "",
      });
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error("Error saving roof measurements:", error);
      toast({
        title: "Error",
        description: "Failed to save roof measurements",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enter Roof Measurements</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="total_squares">
                Total Squares <span className="text-muted-foreground text-xs">(1 square = 100 sq ft)</span>
              </Label>
              <Input
                id="total_squares"
                type="number"
                step="0.01"
                placeholder="e.g., 25"
                value={formData.total_squares}
                onChange={(e) => setFormData({ ...formData, total_squares: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="total_area">Total Area (sq ft)</Label>
              <Input
                id="total_area"
                type="number"
                step="0.01"
                placeholder="e.g., 2500"
                value={formData.total_area}
                onChange={(e) => setFormData({ ...formData, total_area: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pitch">Pitch (e.g., 6/12)</Label>
              <Input
                id="pitch"
                type="text"
                placeholder="e.g., 6/12"
                value={formData.pitch}
                onChange={(e) => setFormData({ ...formData, pitch: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="waste_factor">Waste Factor (%)</Label>
              <Input
                id="waste_factor"
                type="number"
                step="0.1"
                placeholder="e.g., 10"
                value={formData.waste_factor}
                onChange={(e) => setFormData({ ...formData, waste_factor: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="ridges">Ridge Length (ft)</Label>
              <Input
                id="ridges"
                type="number"
                step="0.01"
                placeholder="e.g., 50"
                value={formData.ridges}
                onChange={(e) => setFormData({ ...formData, ridges: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hips">Hip Length (ft)</Label>
              <Input
                id="hips"
                type="number"
                step="0.01"
                placeholder="e.g., 30"
                value={formData.hips}
                onChange={(e) => setFormData({ ...formData, hips: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="valleys">Valley Length (ft)</Label>
              <Input
                id="valleys"
                type="number"
                step="0.01"
                placeholder="e.g., 20"
                value={formData.valleys}
                onChange={(e) => setFormData({ ...formData, valleys: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="eaves">Eave Length (ft)</Label>
              <Input
                id="eaves"
                type="number"
                step="0.01"
                placeholder="e.g., 100"
                value={formData.eaves}
                onChange={(e) => setFormData({ ...formData, eaves: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rakes">Rake Length (ft)</Label>
              <Input
                id="rakes"
                type="number"
                step="0.01"
                placeholder="e.g., 40"
                value={formData.rakes}
                onChange={(e) => setFormData({ ...formData, rakes: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="penetrations">Penetrations (count)</Label>
              <Input
                id="penetrations"
                type="number"
                placeholder="e.g., 3"
                value={formData.penetrations}
                onChange={(e) => setFormData({ ...formData, penetrations: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="stories">Stories</Label>
              <Input
                id="stories"
                type="number"
                placeholder="e.g., 2"
                value={formData.stories}
                onChange={(e) => setFormData({ ...formData, stories: e.target.value })}
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Measurements
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
